import {AEO_DYNAMIC_CONFIG_NAMES} from './constants/aeo';
import {AGENT_ACCESS_DYNAMIC_CONFIG_NAMES} from './constants/agent-access';
import {ANALYZE_DYNAMIC_CONFIG_NAMES} from './constants/analyze';
import {ASSET_DYNAMIC_CONFIG_NAMES} from './constants/assets';
import {CAMPAIGN_DYNAMIC_CONFIG_NAMES} from './constants/campaign';
import {CODE_DYNAMIC_CONFIG_NAMES} from './constants/code';
import {CODE_SYNC_DYNAMIC_CONFIG_NAMES} from './constants/code-sync';
import {CONTENT_DYNAMIC_CONFIG_NAMES} from './constants/content';
import {DEVELOPER_PLATFORM_DYNAMIC_CONFIG_NAMES} from './constants/developer-platform';
import {GOV_DYNAMIC_CONFIG_NAMES} from './constants/gov';
import {LOCALIZATION_DYNAMIC_CONFIG_NAMES} from './constants/localization';
import {ODS_DYNAMIC_CONFIG_NAMES} from './constants/ods';
import {OPTIMIZE_DYNAMIC_CONFIG_NAMES} from './constants/optimize';
import {SP_SUCCESS_DYNAMIC_CONFIG_NAMES} from './constants/sp-success';
import {TRUST_AND_SAFETY_DYNAMIC_CONFIG_NAMES} from './constants/trust-and-safety';
import {WAF_DYNAMIC_CONFIG_NAMES} from './constants/waf';

export const DYNAMIC_CONFIGS = {
  ...AEO_DYNAMIC_CONFIG_NAMES,
  ...AGENT_ACCESS_DYNAMIC_CONFIG_NAMES,
  ...ANALYZE_DYNAMIC_CONFIG_NAMES,
  ...ASSET_DYNAMIC_CONFIG_NAMES,
  ...CAMPAIGN_DYNAMIC_CONFIG_NAMES,
  ...CODE_DYNAMIC_CONFIG_NAMES,
  ...CODE_SYNC_DYNAMIC_CONFIG_NAMES,
  ...CONTENT_DYNAMIC_CONFIG_NAMES,
  ...DEVELOPER_PLATFORM_DYNAMIC_CONFIG_NAMES,
  ...GOV_DYNAMIC_CONFIG_NAMES,
  ...LOCALIZATION_DYNAMIC_CONFIG_NAMES,
  ...ODS_DYNAMIC_CONFIG_NAMES,
  ...OPTIMIZE_DYNAMIC_CONFIG_NAMES,
  ...SP_SUCCESS_DYNAMIC_CONFIG_NAMES,
  ...TRUST_AND_SAFETY_DYNAMIC_CONFIG_NAMES,
  ...WAF_DYNAMIC_CONFIG_NAMES,
  // Operator controls for the cardExpiryNotices daily job (PYMT-6151):
  // dryRun, per-window activationDates, maxSendsPerRun, staleAfterHours.
  // Fail-safe defaults live with the job — absent config = shadow mode.
  CARD_EXPIRY_NOTICES: 'pymt-6144-card-expiry-notices',
  KNOCK_FEED_IPN_SEGMENT_TRACKING:
    'dc-shsvs-8156-knock-feed-ipn-segment-tracking',
  STATSIG_FLAGS_FOR_KNOCK_TARGETING:
    'dc-shsvs-8544-statsig-evals-for-knock-targeting',
  // Emergency operator control: drop form submissions for specific sites/forms
  // before any database writes. Used to respond to spam attacks in real time.
  FORM_SUBMISSION_SPAM_BLOCK: 'dc-forms-spam-site-block',
  // Global ops knobs for dashboard shell SSR. Carries `maxConcurrentRenders`
  // and `maxRendersPerSecond`; the handler supplies fallback values.
  DASHBOARD_SSR: 'dashboard-ssr',
  // Publish checkpoint sampling. logIntervalSecs: intra-stage interval (0 off).
  // elapsedSecs: post-publish offsets ([] off).
  POST_PUBLISH_MEMORY_CHECKPOINTS:
    'dc-wrkf-3995-post-publish-memory-checkpoints',
  // EU sales-contact URL for a blocked enterprise-invite-only signup. Used only
  // when set to an eu.webflow.com URL; otherwise the US default applies.
  // Value shape: {url: string}.
  EU_ENTERPRISE_SALES_CTA: 'dc-gov-1976-eu-enterprise-sales-cta',
  // Sample size for the pruneStaleMongoPublications tenMinutesRunner sweep —
  // docs $sample draws per tick, before the extData/localization filter, not
  // the match count. sampleSize <= 0 is the kill switch. Keep under ~5% of
  // the dynamo_dbs collection (~670K) for $sample's fast path — though
  // MAX_PER_TICK is the real cap, since each match fans out into a
  // sequential schedulePublicationPrunes call (Publication query, Site
  // lookup, S3 manifest fetches).
  // Value shape: {sampleSize: number}.
  CMS_MONGO_PUBLICATION_PRUNE_SWEEP:
    'cont-1494-mongo-publication-prune-sweep-config',
  // Icebox freeze-candidate dry run (cmsIceboxSweep, tenMinutesRunner). Samples
  // staging databases and only logs and counts; never freezes. sampleSize <= 0
  // or an absent config is the kill switch. Keep sampleSize small: each sampled
  // database runs several sequential Postgres queries on the reader. cutoffDays:
  // lastAccessed older than this is cold. maxRowsPerTable: a candidate has every
  // table under this many rows. storageLayerClusterName optionally narrows the
  // sample to one cluster. Only singleTenantPgSelfServe databases are sampled;
  // sampleSize is the draw before that filter.
  // Value shape: {sampleSize: number, cutoffDays: number,
  // maxRowsPerTable: number, storageLayerClusterName?: string}.
  CMS_ICEBOX_SWEEP: 'dc-cont-6672-cms-icebox-sweep',
  // Value shape: {mode: 'off' | 'shadow' | 'enforce'}.
  PAGE_AUTOMATION_TOOL_PERMISSIONS:
    'dc-ext-2629-page-automation-tool-permissions',
  // Outage fallback for promotional entitlements: workspaces/sites that keep
  // access while Stigg is unavailable, keyed by entitlement key.
  // Value shape: {[entitlementKey]: {workspaceIds: string[], siteIds: string[]}}.
  ENTITLEMENT_FALLBACK_CONFIG: 'entitlement-fallback-config',
};

// TODO: Move all calls to `useConfig` to use one of the constants defined here.
export type DynamicConfigName =
  | (typeof DYNAMIC_CONFIGS)[keyof typeof DYNAMIC_CONFIGS]
  | string;
